import { AlertTriangle, ArrowRight, CheckCircle2, Pencil } from "lucide-react";
import { cn, formatUtcDate } from "@/utils/common.utils";
import { buildDealStatus } from "@/utils/deals-command.utils";
import { DEAL_SNAPSHOT_HEALTH } from "@/constant/deals.constant";
import { STATUS_BADGE_TONES } from "@/constant/common.constant";
import StatusBadge from "@/components/common/StatusBadge";
import type { DealSnapshot, Task } from "@/types/deals.types";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

interface DealStatusBannerProps {
  /** Whole checklist — feeds only the progress line, What's next and Completed. */
  tasks: Task[];
  /** Server-computed health (or the agent's override) — the one status the
   *  agent and the client both see. Never derived on the client. */
  snapshot?: DealSnapshot;
  /** Opens the client status-update editor. Omitted for read-only viewers. */
  onEdit?: () => void;
}

export default function DealStatusBanner({ tasks, snapshot, onEdit }: DealStatusBannerProps) {
  const status = buildDealStatus({ tasks });
  const health = snapshot?.health || "on_track";
  const cfg = DEAL_SNAPSHOT_HEALTH[health as keyof typeof DEAL_SNAPSHOT_HEALTH] || DEAL_SNAPSHOT_HEALTH.on_track;
  const atRisk = health === "at_risk";
  const Icon = atRisk ? AlertTriangle : CheckCircle2;
  // Same StatusBadge tone as the header's chip, so both read as one signal.
  const badgeTone = atRisk ? "warning" : "success";
  const tone = STATUS_BADGE_TONES[badgeTone];
  const signals = snapshot?.health_signals ?? [];
  const byAgent = snapshot?.health_source === "agent";
  const message = signals.length
    ? `${signals.join(" · ")}.`
    : atRisk ? "Flagged by the agent — no overdue tasks found." : "Every task is on schedule. Nothing is overdue.";

  const viewAttention = () => {
    document.querySelector('[data-testid="deal-pulse-attention"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border border-dash-border bg-white p-5 md:flex-row md:items-center"
      aria-label="Deal Status"
      data-testid="deal-status-banner"
    >
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-full", tone)} aria-hidden>
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold text-dash-ink">Deal Status</h2>
            <StatusBadge
              tone={badgeTone}
              className="px-2.5 py-1 text-xs font-medium"
              title={byAgent ? "Set by agent" : "Computed from live signals"}
              data-testid="deal-status-chip"
            >
              {cfg.label}
            </StatusBadge>
            {byAgent && <span className="text-xs text-dash-muted">Set by agent</span>}
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-dash-border px-3 py-1.5 text-xs font-medium text-dash-ink transition-colors hover:bg-bone"
                data-testid="deal-snapshot-edit-btn"
              >
                <Pencil className="size-3.5" aria-hidden /> Edit
              </button>
            )}
          </div>
          <p className="mt-1.5 break-words text-sm text-dash-ink" data-testid="deal-status-message">{message}</p>
          {status.total > 0 && (
            <p className="mt-1 text-xs text-dash-muted" data-testid="deal-status-progress">
              {status.doneCount} of {plural(status.total, "task")} done
            </p>
          )}
          {snapshot?.status_note && (
            <p className="mt-3 break-words border-t border-dash-borderSoft pt-3 text-sm italic text-dash-ink" data-testid="deal-snapshot-note">
              “{snapshot.status_note}”
            </p>
          )}
        </div>
      </div>

      {(status.next || status.lastCompleted || atRisk) && (
        <div className="flex min-w-0 shrink-0 flex-col gap-2 border-dash-border text-sm md:w-80 md:border-l md:pl-6">
          {status.next && (
            <div className="flex min-w-0 gap-2" data-testid="deal-status-next">
              <span className="w-20 shrink-0 text-dash-muted">What&apos;s next</span>
              <div className="min-w-0 text-dash-ink">
                <span className="block truncate font-medium">{status.next.title}</span>
                <span className="text-xs text-dash-muted">{status.next.due_date ? `Due ${formatUtcDate(status.next.due_date)}` : "No due date"}</span>
              </div>
            </div>
          )}
          {status.lastCompleted && (
            <div className="flex min-w-0 gap-2" data-testid="deal-status-completed">
              <span className="w-20 shrink-0 text-dash-muted">Completed</span>
              <div className="min-w-0 text-dash-ink">
                <span className="block truncate font-medium">{status.lastCompleted.title}</span>
                {status.lastCompleted.completed_at && (
                  <span className="text-xs text-dash-muted">{formatUtcDate(status.lastCompleted.completed_at)}</span>
                )}
              </div>
            </div>
          )}
          {atRisk && (
            <button
              type="button"
              onClick={viewAttention}
              className="inline-flex items-center gap-1 self-start text-dash-muted transition-colors hover:text-dash-ink"
              data-testid="deal-status-view-open"
            >
              View overdue tasks <ArrowRight className="size-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
